import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Flame, Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { getCurrentGameTimestamp } from '../../../config/dateTimeConfig';
import { usePlayedDays } from '../../../hooks/usePlayedDays';
import { useAuth } from '../../../hooks/useAuth';

interface StreakCalendarProps {
  currentStreak: number;
  maxStreak: number;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const StreakCalendar: React.FC<StreakCalendarProps> = ({
  currentStreak,
  maxStreak,
}) => {
  const { user } = useAuth();
  const today = getCurrentGameTimestamp();
  const [todayYear, todayMonth] = today.split('-').map(Number);

  const [currentYear, setCurrentYear] = useState(todayYear);
  const [currentMonth, setCurrentMonth] = useState(todayMonth - 1); // 0-indexed

  const { playedDates, loading } = usePlayedDays(currentYear, currentMonth);

  const canGoForward =
    currentYear < todayYear ||
    (currentYear === todayYear && currentMonth < todayMonth - 1);

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (!canGoForward) return;
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  // Build calendar grid
  const firstDayOfMonth = new Date(Date.UTC(currentYear, currentMonth, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(currentYear, currentMonth + 1, 0)).getUTCDate();

  const monthName = new Date(Date.UTC(currentYear, currentMonth, 1)).toLocaleString(
    'en-US',
    { month: 'long', timeZone: 'UTC' }
  );

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(d);
  }
  // Pad to complete final row
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const isToday = (day: number) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr === today;
  };

  const isFuture = (day: number) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr > today;
  };

  const isPlayed = (day: number) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return playedDates.has(dateStr);
  };

  const streakAccent = 'text-amber-700 dark:text-amber-300';

  return (
    <div className="w-72 p-4">
      {/* Month navigation header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={goToPreviousMonth}
          className="p-1 rounded hover:bg-muted transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold">
          {monthName} {currentYear}
        </span>
        <button
          onClick={goToNextMonth}
          disabled={!canGoForward}
          className={cn(
            'p-1 rounded transition-colors',
            canGoForward ? 'hover:bg-muted' : 'opacity-30 cursor-not-allowed'
          )}
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAY_LABELS.map((label, i) => (
          <div
            key={i}
            className="w-8 h-6 flex items-center justify-center text-xs text-muted-foreground font-medium"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Date grid */}
      <div className="grid grid-cols-7 gap-0 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {cells.map((day, i) => {
          if (day === null) {
            return <div key={i} className="w-8 h-8" />;
          }

          const played = isPlayed(day);
          const todayDay = isToday(day);
          const future = isFuture(day);

          return (
            <div
              key={i}
              className={cn(
                'w-8 h-8 flex items-center justify-center text-xs rounded-full transition-colors',
                future && 'opacity-30',
                played && 'bg-amber-500 text-white dark:bg-amber-400 dark:text-gray-900 font-semibold',
                todayDay && !played && 'ring-2 ring-amber-500/40',
                !played && !future && 'text-foreground'
              )}
            >
              {day}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-border">
        {user ? (
          <div className={cn('flex items-center justify-center gap-3 text-xs', streakAccent)}>
            <div className="flex items-center gap-1">
              <Flame className="w-3.5 h-3.5" />
              <span className="font-medium">Current: {currentStreak}</span>
            </div>
            <span className="text-muted-foreground">|</span>
            <span className="font-medium">Best: {maxStreak}</span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center">
            Sign in to track your streak
          </p>
        )}
      </div>
    </div>
  );
};
